/**
 * PWA Install Prompt — shows an "Add to Home Screen" banner.
 *
 * Behaviour:
 *  - Android/Chrome: captures beforeinstallprompt → native install dialog.
 *  - iOS Safari: no beforeinstallprompt, so shows a manual instruction banner.
 *  - Shows after 3 s if user hasn't dismissed within the last 7 days.
 *  - Hidden when already running in standalone / installed mode.
 */
import React, { useEffect, useState } from 'react';

const DISMISSED_KEY = 'pwa_install_dismissed_until';
const DISMISS_DAYS  = 7;

function isIos() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}
function isInStandaloneMode() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}
function wasDismissed() {
  const until = localStorage.getItem(DISMISSED_KEY);
  return until && Date.now() < Number(until);
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible]               = useState(false);
  const [iosMode, setIosMode]               = useState(false);

  useEffect(() => {
    if (isInStandaloneMode()) return;   // already installed
    if (wasDismissed()) return;

    const ios = isIos();

    if (ios) {
      // iOS never fires beforeinstallprompt — show manual guide after delay
      setTimeout(() => { setIosMode(true); setVisible(true); }, 3000);
      return;
    }

    // Android / Chrome / Edge — wait for the browser prompt event
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setVisible(true), 3000);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setVisible(false);
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    const until = Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(DISMISSED_KEY, String(until));
    setVisible(false);
  };

  if (!visible) return null;

  const banner = {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 9999,
    background: '#fff',
    borderTop: '1px solid #E2E8F0',
    boxShadow: '0 -4px 16px rgba(0,0,0,0.12)',
    padding: '16px 20px',
    animation: 'slideUpBanner 0.25s ease-out',
  };

  if (iosMode) {
    return (
      <div style={banner}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <div style={{ fontSize: '26px', flexShrink: 0 }}>📲</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: '14px', color: '#1E293B' }}>
              Add to Home Screen
            </div>
            <div style={{ fontSize: '12px', color: '#64748B', marginTop: '4px', lineHeight: 1.5 }}>
              Tap the <strong>Share</strong> button{' '}
              <span style={{ fontSize: '14px' }}>⬆</span> at the bottom of your browser,
              then choose <strong>"Add to Home Screen"</strong>.
            </div>
          </div>
          <button
            onClick={handleDismiss}
            style={{
              background: 'none', border: 'none', fontSize: '18px',
              color: '#94A3B8', cursor: 'pointer', flexShrink: 0, padding: '0 4px',
            }}
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...banner, display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{ fontSize: '28px', flexShrink: 0 }}>📲</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: '14px', color: '#1E293B' }}>
          Install AivioraOps
        </div>
        <div style={{ fontSize: '12px', color: '#64748B', marginTop: '2px' }}>
          Add to your home screen — works offline too.
        </div>
      </div>
      <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
        <button
          onClick={handleDismiss}
          style={{
            background: 'none', border: '1px solid #CBD5E1', borderRadius: '8px',
            padding: '8px 14px', fontSize: '13px', color: '#64748B',
            cursor: 'pointer', whiteSpace: 'nowrap',
          }}
        >
          Not now
        </button>
        <button
          onClick={handleInstall}
          style={{
            background: '#6C63FF', border: 'none', borderRadius: '8px',
            padding: '8px 16px', fontSize: '13px', fontWeight: 600,
            color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap',
          }}
        >
          Install
        </button>
      </div>
    </div>
  );
}
